import Cookie from 'js-cookie';
import { URL_APP } from '@/utils/api/urlApi';
import { chatBaseSegment } from '@/utils/guiareAccess';

/**
 * URL base do chat conforme a persona do usuário
 * (ex.: /api/construtora/chat ou /api/condomino/chat).
 */
function base() {
	return `${URL_APP}/${chatBaseSegment()}/chat`;
}

function authHeaders(extra = {}) {
	const token = Cookie.get('token');
	return {
		Authorization: `Bearer ${token}`,
		...extra,
	};
}

async function jsonOrThrow(response) {
	if (!response.ok) {
		const text = await response.text().catch(() => '');
		throw new Error(`HTTP ${response.status}: ${text || response.statusText}`);
	}
	return response.json();
}

export const chatService = {
	/**
	 * Retorna quais módulos a construtora tem dados (pra montar suggestions).
	 */
	async getContext() {
		const res = await fetch(`${base()}/context`, {
			method: 'GET',
			headers: authHeaders({ Accept: 'application/json' }),
		});
		const body = await jsonOrThrow(res);
		return body?.data?.modulos || {};
	},

	/**
	 * Cria uma nova conversa.
	 */
	async startConversation() {
		const res = await fetch(`${base()}/conversations`, {
			method: 'POST',
			headers: authHeaders({ Accept: 'application/json' }),
		});
		const body = await jsonOrThrow(res);
		return body?.data;
	},

	/**
	 * Lista as conversas do usuário.
	 */
	async listConversations() {
		const res = await fetch(`${base()}/conversations`, {
			method: 'GET',
			headers: authHeaders({ Accept: 'application/json' }),
		});
		const body = await jsonOrThrow(res);
		return body?.data || [];
	},

	/**
	 * Carrega o histórico (mensagens visíveis) de uma conversa.
	 */
	async loadMessages(uuid) {
		const res = await fetch(`${base()}/conversations/${uuid}/messages`, {
			method: 'GET',
			headers: authHeaders({ Accept: 'application/json' }),
		});
		const body = await jsonOrThrow(res);
		return body?.data || { conversation: null, messages: [] };
	},

	/**
	 * Apaga uma conversa.
	 */
	async deleteConversation(uuid) {
		const res = await fetch(`${base()}/conversations/${uuid}`, {
			method: 'DELETE',
			headers: authHeaders({ Accept: 'application/json' }),
		});
		return jsonOrThrow(res);
	},

	/**
	 * Envia uma mensagem e consome o stream SSE.
	 *
	 * @param {string} uuid       UUID da conversa
	 * @param {string} content    Texto do usuário
	 * @param {object} callbacks  { onDelta, onToolStart, onToolEnd, onDone, onError }
	 */
	async streamMessage(uuid, content, callbacks = {}) {
		const { onDelta, onToolStart, onToolEnd, onDone, onError } = callbacks;

		let response;
		try {
			response = await fetch(`${base()}/conversations/${uuid}/messages`, {
				method: 'POST',
				headers: authHeaders({
					'Content-Type': 'application/json',
					Accept: 'text/event-stream',
				}),
				body: JSON.stringify({ content }),
			});
		} catch (networkErr) {
			onError && onError('Não consegui me conectar agora. Verifica sua rede e tenta de novo.');
			return;
		}

		if (!response.ok || !response.body) {
			onError && onError('Tive um problema técnico aqui. Tenta de novo daqui a pouco?');
			return;
		}

		const reader = response.body.getReader();
		const decoder = new TextDecoder('utf-8');
		let buffer = '';

		try {
			let streamDone = false;
			while (!streamDone) {
				const { value, done } = await reader.read();
				if (done) {
					streamDone = true;
					break;
				}

				buffer += decoder.decode(value, { stream: true });

				// SSE events are separated by blank line: "\n\n"
				const blocks = buffer.split('\n\n');
				buffer = blocks.pop(); // last (possibly incomplete) goes back to buffer

				for (const block of blocks) {
					const line = block.trim();
					if (!line || !line.startsWith('data:')) continue;

					const jsonStr = line.slice(5).trim();
					if (!jsonStr) continue;

					let event;
					try {
						event = JSON.parse(jsonStr);
					} catch (_) {
						continue;
					}

					switch (event.type) {
						case 'delta':
							onDelta && onDelta(event.content || '');
							break;
						case 'tool_call_start':
							onToolStart && onToolStart(event.name);
							break;
						case 'tool_call_end':
							onToolEnd && onToolEnd(event.name);
							break;
						case 'done':
							onDone && onDone({
								messageId: event.message_id,
								conversationUuid: event.conversation_uuid,
							});
							break;
						case 'error':
							onError && onError(event.message || 'Algo deu errado.');
							break;
					}
				}
			}
		} catch (streamErr) {
			onError && onError('A conexão foi interrompida. Tenta novamente.');
		}
	},
};

export default chatService;
